(() => {
  'use strict';
  if (!window.customElements || customElements.get('hebio-shipping-progress')) return;

  const subscribers = new Set();
  let listeners;
  let pendingTimer;
  let request;
  let requestTimeout;
  let revision = 0;
  let snapshot;
  let checking = false;
  let unavailable = false;
  const cents = value => Number.isSafeInteger(value) && value >= 0;

  // Retain only the amounts needed by this component, never cart/customer data.
  function normalizeCart(cart) {
    if (!cart || !cents(cart.total_price) || !cents(cart.item_count) ||
        !/^[A-Z]{3}$/.test(cart.currency || '') || !Array.isArray(cart.items) ||
        (cart.item_count === 0) !== (cart.items.length === 0)) return null;
    let nonShippingTotal = 0;
    let requiresShipping = false;
    for (const item of cart.items) {
      if (!item || !cents(item.final_line_price) || typeof item.requires_shipping !== 'boolean') return null;
      if (item.requires_shipping) requiresShipping = true;
      else nonShippingTotal += item.final_line_price;
    }
    if (!cents(nonShippingTotal)) return null;
    return {
      total: Math.max(0, cart.total_price - nonShippingTotal),
      count: cart.item_count,
      currency: cart.currency,
      requiresShipping
    };
  }

  function stopRequest() {
    clearTimeout(pendingTimer);
    pendingTimer = undefined;
    clearTimeout(requestTimeout);
    requestTimeout = undefined;
    request?.abort();
    request = undefined;
    revision += 1;
  }

  function publish(next) {
    stopRequest();
    snapshot = next;
    checking = false;
    unavailable = false;
    subscribers.forEach(element => element.render(next));
  }

  function setChecking() {
    checking = true;
    unavailable = false;
    subscribers.forEach(element => element.renderPending());
  }

  function cartURL() {
    const element = subscribers.values().next().value;
    const url = new URL(element?.dataset.cartUrl || '/cart.js', location.origin);
    if (url.username || url.password || url.origin !== location.origin ||
        !/^\/(?:[a-z0-9-]+\/)?cart\.js$/i.test(url.pathname)) {
      throw new Error('Invalid cart endpoint');
    }
    url.search = '';
    url.hash = '';
    return url.href;
  }

  async function refresh() {
    pendingTimer = undefined;
    if (!subscribers.size || request) return;
    const controller = new AbortController();
    request = controller;
    const expectedRevision = revision;
    let timeout;
    try {
      // An interrupted request must not leave a stale free-shipping promise.
      timeout = setTimeout(() => controller.abort(), 8000);
      requestTimeout = timeout;
      const response = await fetch(cartURL(), {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        redirect: 'error',
        signal: controller.signal
      });
      if (!response.ok) throw new Error('Cart unavailable');
      const next = normalizeCart(await response.json());
      if (!next) throw new Error('Invalid cart response');
      if (expectedRevision === revision && subscribers.size) publish(next);
    } catch (error) {
      if (expectedRevision !== revision || !subscribers.size) return;
      snapshot = undefined;
      checking = false;
      unavailable = true;
      subscribers.forEach(element => element.renderUnavailable());
    } finally {
      clearTimeout(timeout);
      if (request === controller) {
        request = undefined;
        requestTimeout = undefined;
      }
    }
  }

  function scheduleRefresh() {
    if (![...subscribers].some(element => element.supported)) return;
    // A second change can arrive after the previous GET started. Its response
    // predates that change: abort it and let the new debounce read the cart.
    if (request) stopRequest();
    setChecking();
    if (pendingTimer !== undefined) return;
    pendingTimer = setTimeout(refresh, 150);
  }

  function handleCart(event) {
    const next = normalizeCart(event.detail?.cart);
    if (next) publish(next);
    else scheduleRefresh();
  }

  function handleDrawer(event) {
    const next = normalizeCart(event.detail?.cart);
    if (next) {
      publish(next);
      return;
    }
    // A section request may finish after a newer cart mutation or GET. Its
    // Liquid totals must never replace more recent authoritative cart data.
    const rendered = event.target?.querySelector?.('hebio-shipping-progress');
    const renderedSnapshot = rendered?.takeServerSnapshot?.();
    if (checking) return;
    if (renderedSnapshot && snapshot) {
      const unchanged = ['total', 'count', 'currency', 'requiresShipping'].every(key => snapshot[key] === renderedSnapshot[key]);
      if (!unchanged) scheduleRefresh();
    } else if (renderedSnapshot && !unavailable) publish(renderedSnapshot);
    else scheduleRefresh();
  }

  function connect(element) {
    if (!listeners) {
      listeners = new AbortController();
      const options = { signal: listeners.signal };
      document.addEventListener('cart:change', handleCart, options);
      document.addEventListener('cart:refresh', handleCart, options);
      document.addEventListener('cart-drawer:refreshed', handleDrawer, options);
      window.addEventListener('pageshow', event => {
        if (event.persisted) scheduleRefresh();
      }, options);
    }
    subscribers.add(element);
    if (checking) element.renderPending();
    else if (unavailable) element.renderUnavailable();
    else if (snapshot) element.render(snapshot);
  }

  function disconnect(element) {
    subscribers.delete(element);
    if (subscribers.size) return;
    listeners?.abort();
    listeners = undefined;
    stopRequest();
    snapshot = undefined;
    checking = false;
    unavailable = false;
  }

  class HebioShippingProgress extends HTMLElement {
    connectedCallback() {
      this.message = this.querySelector('[data-shipping-message]');
      this.progress = this.querySelector('[data-shipping-progress]');
      this.scope = this.querySelector('[data-shipping-scope]');
      if (!this.message || !this.progress || !this.scope) return;
      this.threshold = Number(this.dataset.thresholdCents);
      this.currency = this.dataset.currency;
      this.supported = this.dataset.country === 'IT' && this.currency === 'EUR' && cents(this.threshold) && this.threshold > 0;
      this.serverSnapshot = {
        total: Number(this.dataset.totalCents),
        count: Number(this.dataset.itemCount),
        currency: this.currency,
        requiresShipping: this.dataset.requiresShipping === 'true'
      };
      if (!cents(this.serverSnapshot.total) || !cents(this.serverSnapshot.count) ||
          !['true', 'false'].includes(this.dataset.requiresShipping)) this.serverSnapshot = null;
      try {
        this.formatter = new Intl.NumberFormat(this.dataset.locale || 'it', { style: 'currency', currency: 'EUR' });
      } catch (error) {
        this.formatter = new Intl.NumberFormat('it', { style: 'currency', currency: 'EUR' });
      }
      connect(this);
      if (this.wasConnected) scheduleRefresh();
      this.wasConnected = true;
    }

    disconnectedCallback() { disconnect(this); }

    takeServerSnapshot() {
      if (this.serverSnapshotUsed) return null;
      this.serverSnapshotUsed = true;
      return this.serverSnapshot;
    }

    money(value) { return this.formatter.format(value / 100); }

    update(state, message, total) {
      this.dataset.state = state;
      if (this.message.textContent !== message) this.message.textContent = message;
      const showProgress = state === 'empty' || state === 'remaining' || state === 'reached';
      this.progress.hidden = !showProgress;
      if (showProgress) {
        this.progress.max = this.threshold;
        this.progress.value = Math.min(this.threshold, Math.max(0, total));
        this.progress.setAttribute('aria-valuetext', message);
      } else {
        this.progress.value = 0;
        this.progress.removeAttribute('aria-valuetext');
      }
    }

    render(next) {
      if (!this.supported || next.currency !== 'EUR') {
        this.scope.textContent = 'Costi e destinazioni';
        this.update('unsupported', 'Spedizione calcolata al checkout.');
        return;
      }
      this.scope.textContent = 'Italia · EUR';
      if (next.count === 0) this.update('empty', `Spedizione gratuita da ${this.money(this.threshold)}.`, 0);
      else if (!next.requiresShipping) this.update('not-required', 'Il carrello non richiede spedizione.');
      else if (next.total >= this.threshold) this.update('reached', 'Hai raggiunto la spedizione gratuita.', next.total);
      else this.update('remaining', `Ti mancano ${this.money(this.threshold - next.total)} per la spedizione gratuita.`, next.total);
    }

    renderPending() {
      if (!this.supported) return;
      this.update('checking', 'Aggiorniamo il totale del carrello…');
    }

    renderUnavailable() {
      if (!this.supported) return;
      this.update('unavailable', 'Verifica nel carrello la spedizione gratuita.');
    }
  }

  customElements.define('hebio-shipping-progress', HebioShippingProgress);
})();
